import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { api } from 'src/api';

// Direct API calls, not a store model: a store model's run() cancels its own in-flight call, and the setup page
// mounts one of these per utility.
const NO_OPTIONS = {
  cancelToken: {}
};
const stopPropagation = event => event.stopPropagation();
const plural = (n, word) => "".concat(n, " ").concat(word).concat(n === 1 ? '' : 's');

// Same wording as the Bills page for a zero-day lag.
const lagText = days => days ? "usually ".concat(plural(days, 'day'), " after the period ends") : 'usually on the day the period ends';

/** Why the estimate is what it is, in words a property manager can challenge. */
export const billReadyReason = settings => {
  switch (settings === null || settings === void 0 ? void 0 : settings.lagBasis) {
    case 'override':
      return 'set by hand to this day of the month';
    case 'utility':
      return "".concat(lagText(settings.lagDays), ", from ").concat(plural(settings.lagSampleCount, 'bill'));
    case 'provider':
      return "".concat(lagText(settings.lagDays), ", from the provider's other bills");
    default:
      return 'no bill dates yet, so this is when the service period ends';
  }
};

/** A whole day 1-31, null for empty (automatic), or undefined when the entry is not valid. */
export const parseDay = text => {
  const trimmed = String(text !== null && text !== void 0 ? text : '').trim();
  if (!trimmed) return null;
  if (!/^\d{1,2}$/.test(trimmed)) return undefined;
  const day = Number(trimmed);
  return day >= 1 && day <= 31 ? day : undefined;
};

// property-service answers errors in PascalCase: { Error: { ErrorCode, GeneralErrorMessage } }.
const messageOf = err => {
  var _err$response;
  const message = err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 || (_err$response = _err$response.data) === null || _err$response === void 0 || (_err$response = _err$response.Error) === null || _err$response === void 0 ? void 0 : _err$response.GeneralErrorMessage;
  return typeof message === 'string' && message.length <= 200 ? message : 'Could not save the bill ready day.';
};

/**
 * The utility's next bill ready date (LB-6032) and the day-of-month override that replaces the estimate. Renders
 * nothing when the estimate cannot be read, so a caller without access sees no broken section.
 */
export const BillReadyDayField = _ref => {
  var _settings$dayOfMonth;
  let propertyUtilityId = _ref.propertyUtilityId,
    className = _ref.className;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    settings = _useState2[0],
    setSettings = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    day = _useState4[0],
    setDay = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    saving = _useState6[0],
    setSaving = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    error = _useState8[0],
    setError = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    readFailed = _useState0[0],
    setReadFailed = _useState0[1];
  const load = useCallback(async () => {
    try {
      var _resp$response;
      const resp = await api.PropertyUtility.getBillReady({
        propertyUtilityId
      }, NO_OPTIONS);
      const data = (_resp$response = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response !== void 0 ? _resp$response : null;
      setSettings(data);
      setDay(data !== null && data !== void 0 && data.dayOfMonth ? String(data.dayOfMonth) : '');
      setReadFailed(false);
    } catch (err) {
      var _err$response2;
      setSettings(null);
      // No access, or no such property utility: nothing to show. Anything else is worth a retry.
      setReadFailed(![403, 404].includes(err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 ? void 0 : _err$response2.status));
    }
  }, [propertyUtilityId]);
  useEffect(() => {
    if (propertyUtilityId) load();
  }, [propertyUtilityId, load]);
  if (propertyUtilityId && readFailed) {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      className: className,
      action: /*#__PURE__*/React.createElement(Button, {
        size: "small",
        onClick: load
      }, "Retry")
    }, "Couldn't load the bill ready date.");
  }
  if (!propertyUtilityId || !settings) return null;
  const parsed = parseDay(day);
  const unchanged = parsed === ((_settings$dayOfMonth = settings.dayOfMonth) !== null && _settings$dayOfMonth !== void 0 ? _settings$dayOfMonth : null);
  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.PropertyUtility.setBillReady({
        propertyUtilityId,
        dayOfMonth: parsed
      }, NO_OPTIONS);
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    /*#__PURE__*/
    // Contained: on the V2 setup page this sits inside an AccordionSummary, which toggles on any click or key that
    // bubbles up to it.
    React.createElement(Stack, {
      spacing: "8px",
      className: className,
      "data-testid": "bill-ready-day-field",
      onClick: stopPropagation,
      onKeyDown: stopPropagation,
      onKeyUp: stopPropagation
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "subtitle2"
    }, "Bill ready"), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, settings.expectedBillDate ? "Next bill ready: ".concat(moment(settings.expectedBillDate).format('MMM D'), " \xB7 ").concat(billReadyReason(settings)) : 'Next bill ready: not enough history yet'), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "8px",
      alignItems: "flex-start"
    }, /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      label: "Bill ready day of month",
      value: day,
      onChange: e => setDay(e.target.value),
      disabled: saving,
      error: parsed === undefined,
      helperText: parsed === undefined ? 'A whole day from 1 to 31' : 'Leave empty to estimate it automatically',
      inputProps: {
        inputMode: 'numeric',
        'aria-label': 'Bill ready day of month'
      },
      sx: {
        width: '280px'
      }
    }), /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: handleSave,
      disabled: saving || parsed === undefined || unchanged
    }, "Save")), error ? /*#__PURE__*/React.createElement(Alert, {
      severity: "error"
    }, error) : null)
  );
};